import { faPencil, faPlus, faTrash, faLock, faUnlock } from "@fortawesome/pro-duotone-svg-icons";
import { Button, createStyles, Flex, ScrollArea, Tooltip } from "@mantine/core";
import { observer } from "mobx-react";
import React, { useRef } from "react";
import { Icon } from "../../../custom-components/icons";
import { QueryKeys, User } from "../../../models/models";
import t from "../../../stores/language";
import { useQuery, useStore } from "../../../stores/store";
import { AddUser, AddUserHandle } from "./add-user";
import CardView, { ItemAction } from "./user-card-view";

export const useStyles = createStyles((theme) => ({
  usersContainer: {
    boxShadow: theme.other.arc.shadows.lg,
    padding: theme.other.arc.paddings[2],
    flex: 1,
    height: "100%",
  },
  addIconButton: {
    color: theme.other.arc.colors[2],
    backgroundColor: "transparent",
    padding: theme.other.arc.paddings[1],
    "&: not([data-disabled]):hover": {
      backgroundColor: "transparent",
    },
  },
  title: {
    padding: `${theme.other.arc.paddings[1]} ${theme.other.arc.paddings[0]}`,
    fontWeight: 500,
    fontSize: theme.fontSizes.lg,
  },
}));

const UsersManager = observer(function UsersManager() {
  const { classes } = useStyles();
  const { authStore } = useStore();
  const addUserRef = useRef<AddUserHandle>(null);
  const { data: users, isLoading: loading } = useQuery({
    queryKey: [QueryKeys.Users],
    queryFn: async () => {
      return await authStore.GetAllUsers();
    },
  });

  const deleteUser = async (user: User) => {
    return await authStore.DeleteUser(user.id);
  };

  const lockUser = async (user: User) => {
    return await authStore.LockUser(user);
  };

  const userActions = (user: User): ItemAction[] => {
    const actions: ItemAction[] = [];
    actions.push({
      icon: faPencil,
      color: "blue",
      text: t("Edit User"),
      action: () => {
        addUserRef.current?.show(user.id);
      },
    });

    actions.push({
      icon: faTrash,
      color: "red",
      text: t("Delete User"),
      askForConfirm: "delete",
      action: () => {
        deleteUser(user);
      },
    });

    actions.push({
      icon: user.lockoutEnabled ? faLock : faUnlock,
      color: user.lockoutEnabled ? "green" : "red",
      text: t(user.lockoutEnabled ? "Lock User" : "Unlock User"),
      askForConfirm: "lock",
      action: () => {
        lockUser(user);
      },
    });
    return actions;
  };
  const userItems = (users ?? [])
    .filter((d) => d.name !== "devuser")
    .map((user) => {
      return {
        user: user,
        actions: userActions(user),
      };
    });
  return loading ? (
    <></>
  ) : (
    <>
      <div className={classes.usersContainer}>
        <Flex direction="row" justify={"space-between"} className={classes.title}>
          <span>{t("Users")} </span>
          <Tooltip label={t("Create User")} withArrow withinPortal>
            <Button size="xs" onClick={() => addUserRef.current?.show()} className={classes.addIconButton}>
              {<Icon size="2x" icon={faPlus}></Icon>}
            </Button>
          </Tooltip>
        </Flex>
        <ScrollArea style={{ height: "calc(100vh - 208px)" }}>
          <CardView users={userItems} />
        </ScrollArea>
      </div>
      <AddUser ref={addUserRef}></AddUser>
    </>
  );
});
export default UsersManager;
